import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { BTN_COLOR, CURRENCIES, THEME_COLOR3, THEME_COLOR4 } from '../../../../../constants';
import type { TModifierView } from '../../modifiers/types';
import type { TMenuItemSlim } from '../types/menu-types';

const currencyList = CURRENCIES as Array<{ code: string; symbol: string }>;

const TOGGLE_FIELDS = [
  { field: 'required', label: 'Required' },
  { field: 'multiSelection', label: 'Multi Select' },
  { field: 'extraAllowed', label: 'Extra Allowed' },
  { field: 'addUpPrices', label: 'Add Up Prices' },
] as const;

type TModifierCardProps = {
  modifier: TModifierView;
  menuItemsById: Map<string, TMenuItemSlim>;
  currencyCode: string;
  onDelete: (id: string) => void;
};

const ModifierCard = ({ modifier, menuItemsById, currencyCode, onDelete }: TModifierCardProps) => {
  const [expanded, setExpanded] = useState(true);
  const currencySymbol =
    currencyList.find((c) => c.code === currencyCode)?.symbol ?? currencyCode;

  const resolvedItems = (modifier.items ?? [])
    .map((id) => menuItemsById.get(id))
    .filter((item): item is TMenuItemSlim => Boolean(item));

  return (
    <Box sx={s.card}>
      {/* Header */}
      <Box sx={s.header}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={s.title} noWrap>
            {modifier.header || 'Unnamed modifier'}
          </Typography>
          <Typography sx={s.subtitle}>
            {resolvedItems.length} item{resolvedItems.length === 1 ? '' : 's'}
          </Typography>
        </Box>
        <IconButton
          size="small"
          color="error"
          onClick={() => onDelete(modifier._idModifier)}
          aria-label="Delete modifier"
          sx={{ flexShrink: 0 }}
        >
          <DeleteIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => setExpanded((p) => !p)}
          sx={{ flexShrink: 0 }}
        >
          {expanded ? (
            <ExpandLessIcon sx={{ fontSize: 16 }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 16 }} />
          )}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Divider sx={{ borderColor: THEME_COLOR4 }} />

        {/* Toggle field grid */}
        <Box sx={s.fieldsGrid}>
          {TOGGLE_FIELDS.map(({ field, label }) => (
            <Box key={field} sx={s.fieldRow}>
              <Typography sx={s.fieldLabel}>{label}</Typography>
              <Typography
                sx={{
                  ...s.fieldValue,
                  color: modifier[field] ? BTN_COLOR : THEME_COLOR3,
                }}
              >
                {modifier[field] ? 'On' : 'Off'}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Items list */}
        {resolvedItems.length > 0 ? (
          <>
            <Divider sx={{ borderColor: THEME_COLOR4 }} />
            {resolvedItems.map((item) => {
              const isDefault = item._id === modifier.defaultSelection;
              return (
                <Box key={item._id} sx={s.itemRow}>
                  {isDefault ? (
                    <CheckCircleIcon sx={{ fontSize: 14, color: BTN_COLOR, flexShrink: 0 }} />
                  ) : (
                    <RadioButtonUncheckedIcon
                      sx={{ fontSize: 14, color: THEME_COLOR4, flexShrink: 0 }}
                    />
                  )}
                  <Typography sx={s.itemName} noWrap>
                    {item.name}
                  </Typography>
                  <Typography sx={s.itemPrice}>
                    {currencySymbol}
                    {(item.price?.value ?? 0).toFixed(2)}
                  </Typography>
                  {isDefault && (
                    <Typography sx={s.defaultBadge}>Default</Typography>
                  )}
                </Box>
              );
            })}
          </>
        ) : (
          <Typography sx={s.emptyItems}>No items attached to this modifier.</Typography>
        )}
      </Collapse>
    </Box>
  );
};

export default ModifierCard;

const s = {
  card: {
    borderRadius: '12px',
    border: `1px solid ${THEME_COLOR4}`,
    backgroundColor: '#FFF',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    px: 2,
    py: 1.5,
    backgroundColor: '#FAFAFA',
  },
  title: { fontFamily: 'GilroySemiBold', fontSize: 13, color: '#000' },
  subtitle: { fontFamily: 'GilroyRegular', fontSize: 11, color: THEME_COLOR3, mt: 0.25 },
  fieldsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 0,
    px: 2,
    py: 1.5,
    columnGap: 3,
    rowGap: 1,
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
  },
  fieldLabel: { fontFamily: 'GilroyRegular', fontSize: 12, color: THEME_COLOR3 },
  fieldValue: { fontFamily: 'GilroySemiBold', fontSize: 12 },
  itemRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    px: 2,
    py: 1,
    borderBottom: `1px solid ${THEME_COLOR4}`,
    '&:last-of-type': { borderBottom: 0 },
  },
  itemName: { flex: 1, fontFamily: 'GilroyRegular', fontSize: 12, color: '#000' },
  itemPrice: { fontFamily: 'GilroySemiBold', fontSize: 12, color: THEME_COLOR3, flexShrink: 0 },
  defaultBadge: {
    fontFamily: 'GilroySemiBold',
    fontSize: 10,
    color: BTN_COLOR,
    backgroundColor: `${BTN_COLOR}18`,
    px: 1,
    py: 0.25,
    borderRadius: '4px',
    flexShrink: 0,
  },
  emptyItems: {
    fontFamily: 'GilroyRegular',
    fontSize: 12,
    color: THEME_COLOR3,
    px: 2,
    py: 1.5,
  },
};
